<template>
    <div class="archive-mode-switch">
        <div class="d-flex align-items-start flex-wrap gap-3">
            <DFormRow
                class="archive-mode-switch__switch"
                is-vertical
                :label="__('messages.archive.switch_label')"
                :hint="__('messages.archive.explanation')"
            >
                <DSwitch
                    id="archive-mode"
                    size="lg"
                    :model-value="modelValue"
                    @update:checked="(value) => $emit('update:modelValue', value)"
                />
            </DFormRow>

            <template v-if="modelValue">
                <DFormRow
                    v-if="showTypeSelect"
                    class="archive-mode-switch__field"
                    is-vertical
                    :label="__('messages.archive.reference_type')"
                    :error="typeError"
                >
                    <DSelect
                        id="archive-reference-type"
                        :model-value="selectedType"
                        :options="typeOptions"
                        @update:modelValue="(value) => $emit('update:selectedType', value || '')"
                    />
                </DFormRow>

                <DFormRow
                    class="archive-mode-switch__field archive-mode-switch__field--hinted"
                    is-vertical
                    :label="__('messages.archive.reference_id')"
                    :hint="showTypeSelect ? __('messages.archive.reference_hint') : __('messages.archive.contract_hint')"
                    :error="referenceIdError"
                >
                    <DInput
                        id="archive-reference-id"
                        :model-value="referenceId"
                        @update:modelValue="(value) => $emit('update:referenceId', value)"
                        @keydown.enter.exact.prevent="pairComplete && $emit('apply')"
                    />
                </DFormRow>
                <div class="archive-mode-switch__action">
                    <span
                        class="archive-mode-switch__action-spacer"
                        aria-hidden="true"
                    />
                    <DButton
                        id="archive-apply"
                        color="primary"
                        :disabled="!pairComplete"
                        :label="__('messages.archive.apply')"
                        @click="$emit('apply')"
                    />
                </div>
            </template>
        </div>

        <DAlert
            v-if="modelValue && readFailed"
            color="warning"
            class="mt-2 mb-0"
        >
            {{ __('messages.archive.read_failed') }}
        </DAlert>
    </div>
</template>

<script setup>

import { computed } from 'vue'

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    types: { type: Array, default: () => [] },
    selectedType: { type: String, default: '' },
    referenceId: { type: [String, Number], default: '' },
    showTypeSelect: { type: Boolean, default: true },
    readFailed: { type: Boolean, default: false },
    typeError: { type: String, default: '' },
    referenceIdError: { type: String, default: '' }
})

defineEmits(['update:modelValue', 'update:selectedType', 'update:referenceId', 'apply'])

const typeOptions = computed(() => props.types.map((type) => ({ label: type, value: type })))

const pairComplete = computed(() => {
    const hasId = props.referenceId !== '' && props.referenceId !== null && props.referenceId !== undefined

    return props.showTypeSelect ? Boolean(props.selectedType) && hasId : hasId
})
</script>

<style lang="scss" scoped>
.archive-mode-switch {
    // Qualified with .d-form-row to outrank DFormRow's own `:not(:last-child)` margin: these rows
    // sit side by side in a flex bar, so the container gap is the only spacing wanted.
    &__field.d-form-row {
        min-width: 12rem;
        margin-bottom: 0;
    }

    // Wider, because this field's hint is a sentence.
    &__field--hinted.d-form-row {
        min-width: 20rem;
    }

    // Stacked like the fields beside it: spacer where their label is, control under it. The
    // gap is DFormRow's own vertical grid-gap, so the button lands exactly on the input line.
    &__action {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    // Reproduces DFormRow's `.label` box -- bold, 10px block padding -- with a non-breaking
    // space for the line itself, so the offset follows the font size instead of being a
    // hard-coded pixel value that drifts the moment the label metrics change.
    &__action-spacer {
        font-weight: bold;
        padding: 10px 0;

        &::before {
            content: "\00a0";
        }
    }

    // Own line, so the paragraph-length explanation does not squeeze the fields beside it.
    &__switch.d-form-row {
        flex-basis: 100%;
        margin-bottom: 0;
    }
}
</style>
